import { addError } from '@shared/utils/logger';
import { getTimezoneFromCoordinates } from '@shared/core/places/timezone';

export interface LocationData
  extends Readonly<{
    lat?: number;
    lng?: number;
  }> {}

/**
 * Fetches timezone using location service wrapper
 * @param location Location object with lat/lng coordinates
 * @param fallbackTimezone Timezone to return if lookup fails
 * @param signal Optional AbortSignal for request cancellation
 * @returns Promise resolving to IANA timezone identifier
 */
export async function getTimezoneFromLocation(location: LocationData | null, fallbackTimezone: string, signal?: AbortSignal): Promise<string> {
  // Explicitly check for null/undefined, not falsy (0 is a valid coordinate)
  if (location?.lat == null || location?.lng == null) {
    return fallbackTimezone;
  }

  try {
    const timeZoneId = await getTimezoneFromCoordinates(location.lat, location.lng, signal);

    // Check if request was aborted
    if (signal?.aborted) {
      return fallbackTimezone;
    }

    return timeZoneId || fallbackTimezone;
  } catch (error) {
    // Don't log AbortErrors - they're intentional cancellations
    if (error instanceof Error && error.name !== 'AbortError') {
      addError(error, {
        context: 'getTimezoneFromLocation',
        lat: location.lat,
        lng: location.lng,
      });
    }
    return fallbackTimezone;
  }
}
